import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import {
  CampaignSchedulerAttribute,
  CampaignType,
} from '@mm/state/model/CampaignsModel';
import { CampaignTypeWithMappedPurposeAndDisplayStatus } from '@mm/state/selectors/campaignSelectors';
import {
  activeChatbotIdSelector,
  getFbPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { Alert, ErrorAlert } from '@mm/components';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import React, { useEffect, useMemo, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { UNREACHABLE_MESSAGE } from '../../../../../common/constants/promotionalMessages';
import { StartBotsDialogueEditor } from '../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import TimelineHeader from './TimelineHeader/TimelineHeader';
import { DialogueSelectorWithReload } from '../../../startBotPromoters/common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { filterCampaignWidgets } from '../helpers';
import { CampaignStatsCompact } from './CampaignStatsCompact/CampaignStatsCompact';
import { SmsIphonePreview } from '../SmsIphonePreview/SmsIphonePreview';
import './DripCampaignTimelineItem.pcss';

type OuterProps = {
  draggableProvided?: DraggableProvided;
  readonly: boolean;
  allowChangeDialogue: boolean;
  showSchedulerStats: boolean;
  campaign?: CampaignTypeWithMappedPurposeAndDisplayStatus;
  scheduler: CampaignSchedulerAttribute;
  onChangeScheduler: (scheduler: CampaignSchedulerAttribute) => void;
  deleteScheduler: (scheduler: CampaignSchedulerAttribute) => void;
  openQueueModal: (schedulerId: number) => void;
  reachable: boolean;
  promotional: boolean;
  savedCampaignCanceled: boolean;
  platforms: { sms: boolean; facebook: boolean; instagram: boolean };
  onSaveCampaign: () => void;
  index: number;
};

type StoreProps = ReturnType<typeof mapStateToProps>;
type ExternalProps = OuterProps;

type InnerProps = StoreProps;

type Props = InnerProps & ExternalProps;

const mapStateToProps = (state: StoreState) => ({
  fbPageId: getFbPageIdSelector(state),
  chatbotId: activeChatbotIdSelector(state),
});

const enhance = compose<InnerProps, OuterProps>(connect(mapStateToProps));

const usePrimeState = (dialogueId: number) => {
  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );
  const dialogues = useSelector((state: StoreState) => state.page.entities);
  const widgetCRUDActions = useWidgetCRUDActions();

  useEffect(() => {
    widgetCRUDActions.retrieveAll(dialogueId);
  }, []);

  return {
    widgets,
    dialogues,
  };
};

const DripCampaignTimelineItem = (props: Props) => {
  const {
    index,
    draggableProvided,
    readonly,
    allowChangeDialogue,
    showSchedulerStats,
    scheduler,
    campaign,
    onChangeScheduler,
    deleteScheduler,
    openQueueModal,
    reachable,
    promotional,
    savedCampaignCanceled,
    chatbotId,
    platforms,
    onSaveCampaign,
  } = props;
  const { widgets, dialogues } = usePrimeState(scheduler.destinationPageId);
  const draggableProps =
    draggableProvided && draggableProvided.draggableProps
      ? draggableProvided.draggableProps
      : {};
  const dragHandleProps =
    draggableProvided && draggableProvided.dragHandleProps
      ? draggableProvided.dragHandleProps
      : {};
  const showUnreachableMessage =
    promotional &&
    (!campaign || !campaign.active || campaign.status === 'paused') &&
    !reachable &&
    (platforms.facebook || platforms.instagram);
  const campaignActive = campaign ? campaign.active : false;
  const paused = campaign ? campaign.status === 'paused' : false;
  const editScheduleDisabled =
    (campaignActive || savedCampaignCanceled) && !paused;

  const pageCRUDActions = usePageCRUDActions();
  const [isExpanded, setIsExpanded] = useState(true);
  useEffect(() => {
    const isFetched = dialogues.some(
      ({ id }) => id === scheduler.destinationPageId,
    );
    // required dialogue not yet fetched
    if (!isFetched) {
      pageCRUDActions.retrieveById(scheduler.destinationPageId);
    }
  }, [campaign, scheduler]);

  useEffect(() => {
    if (!platforms.sms && scheduler.intervalInMin > 1380) {
      onChangeScheduler({ ...scheduler, intervalInMin: 1380 });
    }
  }, [platforms]);

  const dialogueSelectorDropdown = useMemo(() => {
    const campaignDialogueUpdate = async (newDialogueId: number) => {
      if (chatbotId) {
        const newDialogue = dialogues.find(d => d.id === newDialogueId);
        if (newDialogue) {
          onChangeScheduler({
            ...scheduler,
            destinationPageId: newDialogue.id,
            destinationPageName: newDialogue.name,
            destinationPageSearchToken: newDialogue.searchToken,
          });

          return {
            newDialogueId,
          };
        }
      }
    };

    return scheduler.destinationPageId ? (
      <DialogueSelectorWithReload
        dialogueId={scheduler.destinationPageId}
        entityUpdateFunction={campaignDialogueUpdate}
        disabled={editScheduleDisabled}
        forCampaign
        forFacebook
      />
    ) : null;
  }, [scheduler, chatbotId, editScheduleDisabled]);

  const platformsArr = Object.entries(platforms)
    .filter(([_, v]) => v)
    .map(([k, v]) => k);

  const { smsOnly, widgetsToExclude } = filterCampaignWidgets(
    { platforms: platformsArr } as CampaignType,
    widgets,
  );

  widgetsToExclude.push(WidgetTypes.DELAY);

  return (
    <div
      className="timeline-item"
      ref={draggableProvided && draggableProvided.innerRef}
      {...draggableProps}
    >
      {!widgets.length && (
        <Alert
          className="drip-campaign-detail__error"
          type="error"
          showCloseIcon={false}
        >
          Dialogue Empty
        </Alert>
      )}

      <TimelineHeader
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        campaignActive={campaignActive && !paused}
        savedCampaignCanceled={savedCampaignCanceled}
        dragHandleProps={dragHandleProps}
        readonly={readonly && !allowChangeDialogue}
        scheduler={scheduler}
        onChangeScheduler={onChangeScheduler}
        deleteScheduler={deleteScheduler}
        editScheduleDisabled={editScheduleDisabled}
        platforms={platforms}
      />

      {isExpanded && (
        <>
          {campaign && scheduler && showSchedulerStats && (
            <CampaignStatsCompact
              campaign={campaign}
              scheduler={scheduler}
              showQueue={() => openQueueModal(scheduler.id as number)}
            />
          )}

          {showUnreachableMessage && (
            <ErrorAlert message={UNREACHABLE_MESSAGE} />
          )}

          <div className="timeline-item__item-content">
            <div className="timeline-item__dialogue-wrapper">
              {dialogueSelectorDropdown}
              <StartBotsDialogueEditor
                className="timeline-item__dialogue"
                chatbotId={chatbotId}
                specificityId={scheduler.id}
                dialogueId={scheduler.destinationPageId}
                widgets={widgets}
                widgetsToExclude={widgetsToExclude}
                isDisabled={savedCampaignCanceled}
                isSmsRelated={platforms.sms}
                forInstagram={platforms.instagram}
                smsOnly={smsOnly}
                platforms={platformsArr}
              />
            </div>
            {Boolean(platforms.sms) && (
              <SmsIphonePreview
                destinationPageId={scheduler.destinationPageId}
                campaignId={campaign?.id}
                schedulerId={scheduler.id}
                schedulerIndex={index}
                activeTestNumbers={scheduler.testNumbers}
                onSaveCampaign={onSaveCampaign}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default enhance(DripCampaignTimelineItem);
